import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Project, ProjectLibrary } from '../../../types';
import { getProjectLibraries } from '../../../services/projectLibraryService';
import {
  listDriveRoot, listDriveFolder, uploadDriveFile, createDriveFolder, DriveItem,
} from '../../../services/sharepointService';

interface Props { pca: IPublicClientApplication; projectId: string; project: Project; }

interface Crumb { id: string | null; name: string } // id null = drive root

function fmtSize(bytes?: number): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function statusOf(e: unknown): number | null {
  const m = /failed: (\d+)/.exec(e instanceof Error ? e.message : '');
  return m ? Number(m[1]) : null;
}

function FileGlyph({ item }: { item: DriveItem }) {
  const isFolder = !!item.folder;
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={isFolder ? '#0E7490' : '#9198A8'} strokeWidth={1.6} style={{ flexShrink: 0 }}>
      {isFolder
        ? <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
        : <><path d="M14 3v5h5" /><path d="M6 3h8l5 5v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" /></>}
    </svg>
  );
}

export default function DocumentsTab({ pca, projectId }: Props) {
  const [libraries, setLibraries] = useState<ProjectLibrary[] | null>(null);
  const [selected, setSelected] = useState<ProjectLibrary | null>(null);
  const [path, setPath] = useState<Crumb[]>([]); // folder crumbs inside the drive
  const [items, setItems] = useState<DriveItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadName, setUploadName] = useState<string | null>(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load linked libraries once.
  useEffect(() => {
    let cancelled = false;
    getProjectLibraries(pca, projectId)
      .then(libs => { if (!cancelled) { setLibraries(libs); if (libs.length === 1) setSelected(libs[0]); } })
      .catch(() => { if (!cancelled) setLibraries([]); });
    return () => { cancelled = true; };
  }, [pca, projectId]);

  const currentFolderId = path.length > 0 ? path[path.length - 1].id : null;
  const driveId = selected?.dagrun_pm_driveid ?? null;

  const loadItems = useCallback(async () => {
    if (!driveId) return;
    setLoading(true); setError(null); setAccessDenied(false);
    try {
      const list = currentFolderId
        ? await listDriveFolder(pca, driveId, currentFolderId)
        : await listDriveRoot(pca, driveId);
      setItems(list);
    } catch (e) {
      const s = statusOf(e);
      if (s === 401 || s === 403) { setAccessDenied(true); setItems([]); }
      else { setError('Unable to load files — check your connection and try again.'); setItems([]); }
    } finally {
      setLoading(false);
    }
  }, [pca, driveId, currentFolderId]);

  useEffect(() => { if (driveId) loadItems(); }, [driveId, loadItems]);

  const selectLibrary = (lib: ProjectLibrary) => {
    setSelected(lib); setPath([]); setItems(null); setError(null); setAccessDenied(false);
  };
  const openFolder = (item: DriveItem) => setPath(prev => [...prev, { id: item.id, name: item.name }]);
  const goToCrumb = (index: number) => setPath(prev => (index < 0 ? [] : prev.slice(0, index + 1)));

  const onUploadClick = () => fileInputRef.current?.click();
  const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !driveId) return;
    setUploading(true); setUploadName(file.name); setError(null);
    try {
      const buf = await file.arrayBuffer();
      await uploadDriveFile(pca, driveId, currentFolderId, file.name, buf, file.type || 'application/octet-stream');
      await loadItems();
    } catch (err) {
      const s = statusOf(err);
      setError(s === 401 || s === 403
        ? "You don't have permission to access this library."
        : 'Upload failed — please try again.');
    } finally {
      setUploading(false); setUploadName(null);
    }
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name || !driveId || creatingFolder) return;
    setCreatingFolder(true); setError(null);
    try {
      await createDriveFolder(pca, driveId, currentFolderId, name);
      setNewFolderOpen(false); setNewFolderName('');
      await loadItems();
    } catch (err) {
      setError('Could not create the folder — please try again.');
    } finally {
      setCreatingFolder(false);
    }
  };

  return (
    <div style={{ padding: '24px 32px' }}>
      <h3 style={{ margin: 0, marginBottom: 16 }}>Documents</h3>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* Left sidebar — libraries */}
        <div style={{ width: 200, flexShrink: 0 }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>LIBRARIES</div>
          {libraries === null ? (
            <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Loading…</div>
          ) : libraries.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5 }}>
              No libraries connected — add one in Settings.
            </div>
          ) : (
            <div>
              {libraries.map(lib => {
                const isSel = selected?.dagrun_pm_projectlibraryid === lib.dagrun_pm_projectlibraryid;
                return (
                  <button key={lib.dagrun_pm_projectlibraryid} type="button" onClick={() => selectLibrary(lib)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '8px 10px', marginBottom: 4,
                      border: 'none', borderLeft: `3px solid ${isSel ? '#0E7490' : 'transparent'}`,
                      background: isSel ? 'var(--brand-mist)' : 'transparent',
                      color: isSel ? 'var(--brand)' : 'var(--ink-2)',
                      fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: isSel ? 600 : 400,
                      cursor: 'pointer', borderRadius: '0 4px 4px 0',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                    {lib.dagrun_pm_name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Main panel */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {!selected ? (
            <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-4)', fontSize: 14 }}>
              {libraries && libraries.length > 0
                ? 'Select a library to browse files'
                : 'No libraries connected — add one in Settings.'}
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                {/* Breadcrumb */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', fontSize: 13 }}>
                  <CrumbBtn label="Documents" onClick={() => goToCrumb(-1)} active={path.length === 0} />
                  <span style={{ color: 'var(--ink-5)' }}>›</span>
                  <CrumbBtn label={selected.dagrun_pm_name} onClick={() => goToCrumb(-1)} active={path.length === 0} />
                  {path.map((c, i) => (
                    <React.Fragment key={`${c.id}-${i}`}>
                      <span style={{ color: 'var(--ink-5)' }}>›</span>
                      <CrumbBtn label={c.name} onClick={() => goToCrumb(i)} active={i === path.length - 1} />
                    </React.Fragment>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => setNewFolderOpen(o => !o)} style={secBtn}>+ New Folder</button>
                  <button type="button" onClick={onUploadClick} disabled={uploading}
                    style={{ ...primBtn, opacity: uploading ? 0.6 : 1, cursor: uploading ? 'default' : 'pointer' }}>
                    {uploading ? `Uploading${uploadName ? ` ${uploadName}` : ''}…` : 'Upload'}
                  </button>
                  <input ref={fileInputRef} type="file" onChange={onFileChosen} style={{ display: 'none' }} />
                </div>
              </div>

              {newFolderOpen && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                  <input autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') { setNewFolderOpen(false); setNewFolderName(''); } }}
                    placeholder="Folder name"
                    style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, fontFamily: 'var(--font-ui)', minWidth: 220 }} />
                  <button type="button" onClick={createFolder} disabled={!newFolderName.trim() || creatingFolder} style={primBtn}>
                    {creatingFolder ? 'Creating…' : 'Create'}
                  </button>
                  <button type="button" onClick={() => { setNewFolderOpen(false); setNewFolderName(''); }} style={secBtn}>Cancel</button>
                </div>
              )}

              {error && <div className="card" style={{ padding: 12, marginBottom: 12, color: 'var(--danger)', fontSize: 13 }}>{error}</div>}

              {accessDenied ? (
                <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', fontSize: 14 }}>
                  You don't have permission to access this library.
                </div>
              ) : loading && items === null ? (
                <div className="card" style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
              ) : items && items.length === 0 ? (
                <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-4)', fontSize: 14 }}>
                  This library is empty.
                </div>
              ) : (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                        <Th>Name</Th><Th>Modified</Th><Th>Modified By</Th><Th>Size</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {items?.map(item => (
                        <tr key={item.id}
                          onClick={() => item.folder ? openFolder(item) : window.open(item.webUrl, '_blank', 'noopener')}
                          style={{ borderBottom: '0.5px solid var(--border-light)', cursor: 'pointer' }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-2)')}
                          onMouseLeave={e => (e.currentTarget.style.background = '')}>
                          <Td>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              <FileGlyph item={item} />
                              <span style={{ fontSize: 14, color: 'var(--ink)' }}>{item.name}</span>
                              {item.folder && <span style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>{item.folder.childCount ?? 0}</span>}
                            </span>
                          </Td>
                          <Td><span style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
                            {item.lastModifiedDateTime ? new Date(item.lastModifiedDateTime).toLocaleDateString() : '—'}
                          </span></Td>
                          <Td><span style={{ fontSize: 13, color: 'var(--ink-3)' }}>{item.lastModifiedBy?.user?.displayName ?? '—'}</span></Td>
                          <Td><span style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>{item.folder ? '—' : fmtSize(item.size)}</span></Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const primBtn: React.CSSProperties = {
  background: '#0E7490', color: '#fff', border: 'none', padding: '7px 14px',
  borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const secBtn: React.CSSProperties = {
  background: '#fff', color: '#0E7490', border: '1px solid #0E7490', padding: '7px 14px',
  borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

function CrumbBtn({ label, onClick, active }: { label: string; onClick: () => void; active: boolean }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px',
        fontFamily: 'var(--font-ui)', fontSize: 13,
        fontWeight: active ? 600 : 400,
        color: active ? 'var(--ink)' : '#0E7490',
        maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{label}</button>
  );
}

const Th = ({ children }: { children: React.ReactNode }) => (
  <th style={{ padding: '10px 14px', fontWeight: 500, fontSize: 12, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</th>
);
const Td = ({ children }: { children: React.ReactNode }) => (
  <td style={{ padding: '10px 14px', verticalAlign: 'middle' }}>{children}</td>
);
